import { cn } from '@/lib/utils'
import React from 'react'

type ItemType = {
    item: true
    xs?: number | 'auto'
    sm?: number | 'auto'
    md?: number | 'auto'
    lg?: number | 'auto'
    xl?: number | 'auto'
}
type ContainerType = { container: true }

type Props = (ItemType | ContainerType) & {
    className?: string
    children: React.ReactNode
    columnSpacing?: number
    rowSpacing?: number
    spacing?: number
    columns?: number | { xs?: number; md?: number }
}

/**
 * @example
 * <Grid container>
 *   <Grid item xs={12} sm={6}>
 *     <FormField name="usuario" label="Usuário" type="text" />
 *   </Grid>
 *   <Grid item xs={12} sm={6}>
 *     <FormField name="senha" label="Senha" type="password" />
 *   </Grid>
 * </Grid>
 */
const Grid = React.forwardRef<HTMLDivElement, Props>(
    (
        {
            children,
            className,
            columns = 12,
            spacing,
            columnSpacing,
            rowSpacing,
            ...rest
        },
        ref,
    ) => {
        const isContainer = (rest as ContainerType).container
        const gridItemId = React.useId()

        if (isContainer) spacing ??= 2
        spacing ??= 0

        // Generate CSS for responsive grid items
        const generateResponsiveCSS = () => {
            if (!('item' in rest)) return null

            const styles: string[] = []

            if (rest.xs != null) {
                const value =
                    rest.xs === 'auto' ? 'auto / auto' : `span ${rest.xs}`
                styles.push(
                    `#${CSS.escape(gridItemId)} { grid-column: ${value}; }`,
                )
            }

            if (rest.sm != null) {
                const value =
                    rest.sm === 'auto' ? 'auto / auto' : `span ${rest.sm}`
                styles.push(
                    `@media (min-width: 640px) { #${CSS.escape(
                        gridItemId,
                    )} { grid-column: ${value}; } }`,
                )
            }

            if (rest.md != null) {
                const value =
                    rest.md === 'auto' ? 'auto / auto' : `span ${rest.md}`
                styles.push(
                    `@media (min-width: 768px) { #${CSS.escape(
                        gridItemId,
                    )} { grid-column: ${value}; } }`,
                )
            }

            if (rest.lg != null) {
                const value =
                    rest.lg === 'auto' ? 'auto / auto' : `span ${rest.lg}`
                styles.push(
                    `@media (min-width: 1024px) { #${CSS.escape(
                        gridItemId,
                    )} { grid-column: ${value}; } }`,
                )
            }

            if (rest.xl != null) {
                const value =
                    rest.xl === 'auto' ? 'auto / auto' : `span ${rest.xl}`
                styles.push(
                    `@media (min-width: 1280px) { #${CSS.escape(
                        gridItemId,
                    )} { grid-column: ${value}; } }`,
                )
            }

            return styles.length > 0 ? <style>{styles.join('\n')}</style> : null
        }

        return (
            <>
                {generateResponsiveCSS()}
                <div
                    ref={ref}
                    id={'item' in rest ? gridItemId : undefined}
                    className={cn('grid w-full h-fit min-h-fit', className)}
                    style={{
                        gridTemplateColumns: isContainer
                            ? `repeat(${columns}, minmax(0, 1fr))`
                            : undefined,
                        columnGap: columnSpacing
                            ? `${columnSpacing * 4}px`
                            : `${spacing * 4}px`,
                        rowGap: rowSpacing
                            ? `${rowSpacing * 4}px`
                            : `${spacing * 4}px`,
                    }}
                >
                    {children}
                </div>
            </>
        )
    },
)

Grid.displayName = 'Grid'

export default Grid
